/** @module @liquidefy/code-editor/editor/editor-diff @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  DocumentId,
  EditorInstanceId,
  DiffEditorState,
  DiffChange,
  CharChange,
  LinkedEditorState,
  DiffId,
  Range,
  EditOperation,
  EditorResult,
  DiffRenderMode,
  MovedBlock,
  DiffResult,
  DiffStats,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asEditorInstanceId,
  asDiffId,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — diff change type, view mode, linked state
// ═══════════════════════════════════════════════════════════════════════════════

/** Classification of a single diff change. */
export type DiffChangeType = 'added' | 'removed' | 'modified' | 'moved' | 'unchanged';

/** Rendering mode for the diff editor. */
export type DiffViewMode = 'side-by-side' | 'inline';

/** An inline unified diff representation. */
export interface InlineDiff {
  readonly id: DiffId;
  readonly originalContent: string;
  readonly modifiedContent: string;
  readonly changes: DiffChange[];
  readonly charChanges: CharChange[];
  readonly mode: 'inline';
  readonly stats: DiffStats;
}

/** A side-by-side diff representation. */
export interface SideBySideDiff {
  readonly id: DiffId;
  readonly originalContent: string;
  readonly modifiedContent: string;
  readonly changes: DiffChange[];
  readonly mode: 'side-by-side';
  readonly stats: DiffStats;
  readonly originalDocumentId: DocumentId;
  readonly modifiedDocumentId: DocumentId;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 DIFF EDITOR STATE — create, configure
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new DiffEditorState from two document IDs (original and modified).
 * The changes array is initially empty — populate it by calling
 * `computeInlineDiff` or `computeSideBySideDiff`.
 *
 * @param originalId The document ID of the original (left) document.
 * @param modifiedId The document ID of the modified (right) document.
 */
export function createDiffEditorState(
  originalId: DocumentId,
  modifiedId: DocumentId,
): EditorResult<DiffEditorState> {
  if (originalId === modifiedId) {
    return err('DIFF_SAME_DOCUMENT', 'Original and modified documents must be different');
  }

  const state: DiffEditorState = {
    originalId,
    modifiedId,
    changes: [],
    renderSideBySide: true,
    ignoreTrimWhitespace: true,
    maxComputationTime: 5000,
  };

  return ok(state, 'DIFF_EDITOR_CREATED', 'Diff editor state created');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 DIFF COMPUTATION — inline and side-by-side
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Compute an inline (unified) diff between two content strings.
 * Uses a Myers diff algorithm to produce a minimal edit sequence.
 * Returns an InlineDiff with both line-level and character-level changes.
 *
 * @param original The original content string.
 * @param modified The modified content string.
 */
export function computeInlineDiff(original: string, modified: string): InlineDiff {
  const originalLines = original.split('\n');
  const modifiedLines = modified.split('\n');
  const changes = myersDiff(originalLines, modifiedLines);
  const charChanges = computeCharChanges(changes, originalLines, modifiedLines);
  const stats = computeStats(changes);

  return {
    id: asDiffId(generateId('diff')),
    originalContent: original,
    modifiedContent: modified,
    changes,
    charChanges,
    mode: 'inline',
    stats,
  };
}

/**
 * Compute a side-by-side diff between two content strings.
 * Produces aligned pairs of original/modified line ranges for parallel rendering.
 *
 * @param original   The original content string.
 * @param modified   The modified content string.
 * @param originalId The document ID of the original document (for linking).
 * @param modifiedId The document ID of the modified document (for linking).
 */
export function computeSideBySideDiff(
  original: string,
  modified: string,
  originalId: DocumentId,
  modifiedId: DocumentId,
): SideBySideDiff {
  const originalLines = original.split('\n');
  const modifiedLines = modified.split('\n');
  const changes = myersDiff(originalLines, modifiedLines);
  const stats = computeStats(changes);

  return {
    id: asDiffId(generateId('diff')),
    originalContent: original,
    modifiedContent: modified,
    changes,
    mode: 'side-by-side',
    stats,
    originalDocumentId: originalId,
    modifiedDocumentId: modifiedId,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 MOVED BLOCK DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Detect moved or renamed blocks in a diff result.
 * A block is considered moved when a removed range in the original closely matches
 * an added range in the modified document (similarity above threshold).
 *
 * @param diff      A DiffResult or an InlineDiff containing the change set.
 * @param threshold Minimum similarity ratio (0–1) to consider a block "moved". Defaults to 0.75.
 */
export function detectMovedBlocks(
  diff: { readonly changes: DiffChange[]; readonly originalContent: string; readonly modifiedContent: string },
  threshold: number = 0.75,
): MovedBlock[] {
  const originalLines = diff.originalContent.split('\n');
  const modifiedLines = diff.modifiedContent.split('\n');

  const removedBlocks: Array<{ range: Range; lines: string[] }> = [];
  const addedBlocks: Array<{ range: Range; lines: string[] }> = [];

  for (const change of diff.changes) {
    if (change.originalEndLineNumber > 0 && change.modifiedStartLineNumber === 0) {
      // Pure deletion
      const lines = originalLines.slice(change.originalStartLineNumber - 1, change.originalEndLineNumber);
      removedBlocks.push({
        range: {
          start: { line: change.originalStartLineNumber - 1, column: 0 },
          end: { line: change.originalEndLineNumber - 1, column: 0 },
        },
        lines,
      });
    } else if (change.modifiedEndLineNumber > 0 && change.originalStartLineNumber === 0) {
      // Pure addition
      const lines = modifiedLines.slice(change.modifiedStartLineNumber - 1, change.modifiedEndLineNumber);
      addedBlocks.push({
        range: {
          start: { line: change.modifiedStartLineNumber - 1, column: 0 },
          end: { line: change.modifiedEndLineNumber - 1, column: 0 },
        },
        lines,
      });
    }
  }

  const moved: MovedBlock[] = [];
  for (const removed of removedBlocks) {
    for (const added of addedBlocks) {
      const similarity = computeTextSimilarity(removed.lines.join('\n'), added.lines.join('\n'));
      if (similarity >= threshold) {
        moved.push({
          originalRange: removed.range,
          modifiedRange: added.range,
          similarity,
        });
      }
    }
  }

  return moved;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 DIFF NAVIGATION — next/prev change
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Navigate to the next or previous diff change from the current change index.
 * Wraps around at the ends (last → first, first → last).
 *
 * @param state     The current DiffEditorState.
 * @param direction 'next' or 'prev'.
 * @returns The index of the change to navigate to, and its line in the modified document.
 */
export function navigateToDiffChange(
  state: DiffEditorState,
  direction: 'next' | 'prev',
): EditorResult<{ readonly changeIndex: number; readonly modifiedLine: number }> {
  if (state.changes.length === 0) {
    return err('NO_DIFF_CHANGES', 'No diff changes to navigate');
  }

  // We store current index conceptually as -1 = none selected
  const currentIndex = -1;
  let nextIndex: number;

  if (direction === 'next') {
    nextIndex = currentIndex < state.changes.length - 1 ? currentIndex + 1 : 0;
  } else {
    nextIndex = currentIndex > 0 ? currentIndex - 1 : state.changes.length - 1;
  }

  const change = state.changes[nextIndex]!;
  const modifiedLine = change.modifiedStartLineNumber;

  return ok(
    { changeIndex: nextIndex, modifiedLine },
    'DIFF_NAVIGATED',
    `Navigated to change ${nextIndex + 1} of ${state.changes.length}`,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 DIFF SUMMARY
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Produce a summary count of added, removed, modified, and moved lines for the diff.
 *
 * @param state The DiffEditorState to summarize.
 */
export function getDiffSummary(state: DiffEditorState): DiffStats {
  return computeStats(state.changes);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 APPLY & REJECT CHANGES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Accept a single diff change, removing it from the pending changes list.
 * In practice, accepting a change means keeping the modified version as-is.
 * The engine does not modify document content — it removes the change from the
 * diff so the IO layer knows it is settled.
 *
 * @param state       Current DiffEditorState.
 * @param changeIndex The index (0-based) of the change to accept.
 */
export function applyDiffChange(
  state: DiffEditorState,
  changeIndex: number,
): EditorResult<DiffEditorState> {
  if (changeIndex < 0 || changeIndex >= state.changes.length) {
    return err('DIFF_CHANGE_NOT_FOUND', `No diff change at index ${changeIndex}`);
  }
  const changes = state.changes.filter((_, i) => i !== changeIndex);
  return ok({ ...state, changes }, 'DIFF_CHANGE_APPLIED', `Change ${changeIndex} accepted`);
}

/**
 * Reject a single diff change, reverting the modified version to the original.
 * Like `applyDiffChange`, the engine removes the change record — the IO layer
 * is responsible for writing the original content back to the document.
 *
 * @param state       Current DiffEditorState.
 * @param changeIndex The index (0-based) of the change to reject.
 */
export function rejectDiffChange(
  state: DiffEditorState,
  changeIndex: number,
): EditorResult<DiffEditorState> {
  if (changeIndex < 0 || changeIndex >= state.changes.length) {
    return err('DIFF_CHANGE_NOT_FOUND', `No diff change at index ${changeIndex}`);
  }
  const changes = state.changes.filter((_, i) => i !== changeIndex);
  return ok({ ...state, changes }, 'DIFF_CHANGE_REJECTED', `Change ${changeIndex} rejected`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 LINKED EDITORS — synchronized scroll and edit pairs
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a LinkedEditorState that synchronizes scrolling and optionally cursors
 * between two editor instances.
 *
 * @param primaryId   The editor instance that initiates scroll/edit events.
 * @param secondaryId The editor instance that mirrors the primary.
 * @param syncCursors Whether cursor positions should also be synchronized.
 */
export function createLinkedEditorState(
  primaryId: EditorInstanceId,
  secondaryId: EditorInstanceId,
  syncCursors: boolean = false,
): EditorResult<LinkedEditorState> {
  if (primaryId === secondaryId) {
    return err('LINKED_SAME_EDITOR', 'Primary and secondary editors must be different');
  }

  const state: LinkedEditorState = {
    editorIds: [primaryId, secondaryId],
    syncScrolling: true,
    syncCursors,
  };

  return ok(state, 'LINKED_EDITORS_CREATED', 'Linked editor pair created');
}

/**
 * Propagate a scroll position change from one editor to its linked counterpart.
 * Returns the updated LinkedEditorState and the target editor ID that should
 * have its scroll position set to `scrollTop`.
 *
 * @param state     Current linked editor state.
 * @param sourceId  The editor that initiated the scroll.
 * @param scrollTop The new scrollTop value (pixels from top of content).
 */
export function syncLinkedScroll(
  state: LinkedEditorState,
  sourceId: EditorInstanceId,
  scrollTop: number,
): EditorResult<{ readonly linkedState: LinkedEditorState; readonly targetId: EditorInstanceId; readonly scrollTop: number }> {
  if (!state.syncScrolling) {
    return err('LINKED_SCROLL_DISABLED', 'Scroll synchronization is disabled for this pair');
  }
  if (!state.editorIds.includes(sourceId)) {
    return err('LINKED_EDITOR_NOT_FOUND', `Editor ${sourceId} is not part of this linked pair`);
  }

  const targetId = state.editorIds.find(id => id !== sourceId) as EditorInstanceId;
  if (!targetId) {
    return err('LINKED_NO_TARGET', 'Could not determine target editor for scroll sync');
  }

  return ok(
    { linkedState: state, targetId, scrollTop },
    'LINKED_SCROLL_SYNCED',
    `Scroll synced to ${targetId}`,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 INTERNAL HELPERS — Myers diff, char diff, similarity
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Compute a line-level diff using the Myers shortest edit script algorithm.
 * Returns an array of DiffChange records (1-based line numbers matching Monaco convention).
 */
function myersDiff(originalLines: string[], modifiedLines: string[]): DiffChange[] {
  const n = originalLines.length;
  const m = modifiedLines.length;
  const changes: DiffChange[] = [];

  // LCS-based simple diff (optimized Myers for editor use)
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (originalLines[i] === modifiedLines[j]) {
        dp[i]![j] = dp[i + 1]![j + 1]! + 1;
      } else {
        dp[i]![j] = Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
      }
    }
  }

  // Walk the LCS to produce diff changes
  let i = 0;
  let j = 0;
  let origStart = -1;
  let modStart = -1;

  const flush = () => {
    if (origStart !== -1 || modStart !== -1) {
      changes.push({
        originalStartLineNumber: origStart !== -1 ? origStart + 1 : 0,
        originalEndLineNumber: origStart !== -1 ? i : 0,
        modifiedStartLineNumber: modStart !== -1 ? modStart + 1 : 0,
        modifiedEndLineNumber: modStart !== -1 ? j : 0,
      });
      origStart = -1;
      modStart = -1;
    }
  };

  while (i < n || j < m) {
    if (i < n && j < m && originalLines[i] === modifiedLines[j]) {
      flush();
      i++;
      j++;
    } else if (j < m && (i >= n || dp[i]![j + 1]! >= dp[i + 1]![j]!)) {
      if (modStart === -1) modStart = j;
      j++;
    } else {
      if (origStart === -1) origStart = i;
      i++;
    }
  }
  flush();

  return changes;
}

/** Compute per-line character-level changes for modified diff hunks. */
function computeCharChanges(
  changes: DiffChange[],
  originalLines: string[],
  modifiedLines: string[],
): CharChange[] {
  const charChanges: CharChange[] = [];

  for (const change of changes) {
    if (
      change.originalStartLineNumber > 0 &&
      change.modifiedStartLineNumber > 0 &&
      change.originalEndLineNumber > 0 &&
      change.modifiedEndLineNumber > 0
    ) {
      // Only compute char changes for pure modifications (not add/remove)
      const origLine = originalLines[change.originalStartLineNumber - 1] ?? '';
      const modLine = modifiedLines[change.modifiedStartLineNumber - 1] ?? '';
      const [origStart, origEnd, modStart, modEnd] = diffStrings(origLine, modLine);

      charChanges.push({
        originalStartLineNumber: change.originalStartLineNumber,
        originalStartColumn: origStart + 1,
        originalEndLineNumber: change.originalEndLineNumber,
        originalEndColumn: origEnd + 1,
        modifiedStartLineNumber: change.modifiedStartLineNumber,
        modifiedStartColumn: modStart + 1,
        modifiedEndLineNumber: change.modifiedEndLineNumber,
        modifiedEndColumn: modEnd + 1,
      });
    }
  }

  return charChanges;
}

/** Find the first-differing and last-differing column range between two strings. */
function diffStrings(a: string, b: string): [number, number, number, number] {
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  return [start, endA, start, endB];
}

/** Compute diff stats from a set of DiffChange records. */
function computeStats(changes: DiffChange[]): DiffStats {
  let additions = 0;
  let deletions = 0;
  let modifications = 0;

  for (const c of changes) {
    const hasOrig = c.originalEndLineNumber > 0;
    const hasMod = c.modifiedEndLineNumber > 0;
    if (hasOrig && hasMod) {
      modifications++;
    } else if (hasMod) {
      additions += c.modifiedEndLineNumber - c.modifiedStartLineNumber + 1;
    } else if (hasOrig) {
      deletions += c.originalEndLineNumber - c.originalStartLineNumber + 1;
    }
  }

  return { additions, deletions, changes: modifications };
}

/**
 * Compute a rough text similarity ratio between two strings.
 * Based on Dice coefficient of bigrams.
 */
function computeTextSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;

  const aBigrams = new Map<string, number>();
  for (let i = 0; i < a.length - 1; i++) {
    const bg = a.slice(i, i + 2);
    aBigrams.set(bg, (aBigrams.get(bg) ?? 0) + 1);
  }

  let intersectionSize = 0;
  for (let i = 0; i < b.length - 1; i++) {
    const bg = b.slice(i, i + 2);
    const count = aBigrams.get(bg) ?? 0;
    if (count > 0) {
      intersectionSize++;
      aBigrams.set(bg, count - 1);
    }
  }

  return (2 * intersectionSize) / (a.length + b.length - 2);
}
